import React from 'react';

// import axios, {
//   AxiosInstance,
// } from 'axios';

import {
  Alert,
  Box,
  Button,
  Chip,
  Collapse,
  Divider,
  IconButton,
  InputAdornment,
  LinearProgress,
  MenuItem,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

import ArrowBackOutlinedIcon from '@mui/icons-material/ArrowBackOutlined';
import ClearOutlinedIcon from '@mui/icons-material/ClearOutlined';
import DatasetOutlinedIcon from '@mui/icons-material/DatasetOutlined';
import ExpandLessOutlinedIcon from '@mui/icons-material/ExpandLessOutlined';
import ExpandMoreOutlinedIcon from '@mui/icons-material/ExpandMoreOutlined';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import LinkOffOutlinedIcon from '@mui/icons-material/LinkOffOutlined';
import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined';
import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

import {
  BieRecordId,
  IBieApiResponse,
  getBieImportClassLabel,
} from './bieonboardingworkbench.models';

import BieOnboardingStatusChip from './BieOnboardingStatusChip';
import { fetchBieOnboardingImportItems } from './bieonboardingworkbench.apis';
import { FWorkbenchShell } from '../workbench';
import {
  IBieOnboardingImportRecord,
  IBieOnboardingImportRecordsProps,
  IBieOnboardingImportRecordsState,
} from './bieonboarding.import.models';

/*
 * Adjust this import to the shared Axios client used by the
 * application.
 */
// import apiClient from '../../../services/apiClient';

/* ============================================================
 * API configuration
 * ============================================================
 */

//const DEFAULT_IMPORT_ITEMS_ENDPOINT = '/api/bie/onboarding/import-items';
const PAGE_REVIEW_WORKBENCH = 'bieonboardingreview';
const PAGE_IMPORT_ITEM = 'bieonboardingimportitem';

/* ============================================================
 * Component
 * ============================================================
 */

/**
 * Browser for the staged rows belonging to one onboarding
 * Import.
 *
 * Supports:
 *
 * - Validation-state filtering
 * - Resolution filtering
 * - Record-class filtering
 * - Search
 * - Server-side pagination
 * - Raw-data inspection
 * - Validation-detail inspection
 * - Navigation to the individual Import Item
 */
export default class BieOnboardingImportRecords extends React.Component<
  IBieOnboardingImportRecordsProps,
  IBieOnboardingImportRecordsState
> {
  // private readonly httpClient:
  //   AxiosInstance;

  private mounted = false;

  private requestVersion = 0;

  public constructor(props: IBieOnboardingImportRecordsProps) {
    super(props);

    // this.httpClient =
    //   props.httpClient ||
    //   apiClient;

    this.state = {
      loading: false,
      refreshing: false,

      errorMessage: '',

      records: [],

      filters: {
        validationCode: '',
        resolutionStatus: '',
        recordClassCode: '',
        search: '',
      },

      pageOffset: 0,
      pageLimit: 100,

      expandedRecordId: '',
      selectedRecordId: '',
    };
  }

  public componentDidMount(): void {
    this.mounted = true;

    void this.loadRecords(true);
  }

  public componentDidUpdate(
    previousProps: IBieOnboardingImportRecordsProps
  ): void {
    if (
      String(this.resolveImportId(previousProps)) !==
      String(this.resolveImportId(this.props))
    ) {
      this.requestVersion += 1;

      this.setState(
        {
          loading: false,
          refreshing: false,

          errorMessage: '',

          records: [],

          filters: {
            validationCode: '',
            resolutionStatus: '',
            recordClassCode: '',
            search: '',
          },

          pageOffset: 0,
          pageLimit: 100,

          expandedRecordId: '',
          selectedRecordId: '',
        },
        () => {
          void this.loadRecords(true);
        }
      );
    }
  }

  public componentWillUnmount(): void {
    this.mounted = false;
    this.requestVersion += 1;
  }

  public render(): React.ReactNode {

    const back = (
      <Button
        variant="outlined"
        startIcon={<ArrowBackIcon />}
        onClick={this.handleBack}
      >
        Back to Onboarding Review
      </Button>
    );

    return (
      <FWorkbenchShell
        //title={this.props.title || this.props.params?.sheetname || 'Onboarding Import Records'}
        title={'Institutional Onboarding Workbench - Imports'}
        subtitle="Review the staged rows belonging to the selected workbook import."
        icon={<DatasetOutlinedIcon />}
        loading={this.state.loading && this.state.records.length === 0}
        error={this.state.errorMessage}
        onBack={this.handleBack}
        onRefresh={this.handleRefresh}
      >
      <Stack spacing={2}>
        {this.renderHeader()}

        <Paper
          variant="outlined"
          sx={{
            overflow: 'hidden',
          }}
        >
          {this.state.refreshing && <LinearProgress />}

          <Box
            sx={{
              p: 2,
            }}
          >
            {this.renderFilters()}
          </Box>

          {this.state.errorMessage && (
            <Box
              sx={{
                px: 2,
                pb: 2,
              }}
            >
              <Alert severity="error">{this.state.errorMessage}</Alert>
            </Box>
          )}

          {this.state.loading
            ? this.renderLoadingTable()
            : this.renderRecordsTable()}

          {this.renderPagination()}
        </Paper>
      </Stack>
      </FWorkbenchShell>
    );
  }

  private resolveImportId(
    props: IBieOnboardingImportRecordsProps = this.props
  ): BieRecordId {
    return props.importId ?? props.params?.importid ?? '';
  }

  private readonly handleBack = (): void => {
    console.log('handling back in importrecords');
    const returnpage = this.props.params?.returnpage;
    if(returnpage){
      this.props.setPage(returnpage);
      
    }
    if (this.props.onBack) {
      this.props.onBack();
      return;
    }

    console.log('using setpage to go back');
    const setParam = this.props.onParam('set');
    setParam('loadsessionid', this.props.params?.loadsessionid || null);
    setParam('stp', this.props.params?.stp || null);
    this.props.setPage(PAGE_REVIEW_WORKBENCH);
  };

  /* ==========================================================
   * Header
   * ==========================================================
   */

  private renderHeader(): React.ReactNode {
    const firstRecord = this.state.records[0];

    return (
      <Paper
        variant="outlined"
        sx={{
          p: {
            xs: 1.5,
            sm: 2,
          },
        }}
      >
        <Stack
          direction={{
            xs: 'column',
            sm: 'row',
          }}
          justifyContent="space-between"
          alignItems={{
            xs: 'stretch',
            sm: 'center',
          }}
          spacing={2}
        >
          <Stack
            direction="row"
            alignItems="center"
            spacing={1.25}
            sx={{
              minWidth: 0,
            }}
          >
            {this.props.onBack && (
              <Tooltip title="Back" arrow>
                <IconButton
                  disabled={this.isBusy()}
                  onClick={this.props.onBack}
                >
                  <ArrowBackOutlinedIcon />
                </IconButton>
              </Tooltip>
            )}

            <Box
              sx={{
                width: 42,
                height: 42,

                display: {
                  xs: 'none',
                  sm: 'flex',
                },

                alignItems: 'center',
                justifyContent: 'center',

                flexShrink: 0,

                borderRadius: 2,

                color: 'primary.main',

                backgroundColor: 'action.hover',
              }}
            >
              <DatasetOutlinedIcon />
            </Box>

            <Box
              sx={{
                minWidth: 0,
              }}
            >
              <Typography variant="h5" fontWeight={700} noWrap>
                {firstRecord?.importName ||
                  firstRecord?.importCode ||
                  `Import ${String(this.resolveImportId())}`}
              </Typography>

              <Stack
                direction="row"
                spacing={0.75}
                alignItems="center"
                useFlexGap
                flexWrap="wrap"
                sx={{
                  mt: 0.4,
                }}
              >
                {firstRecord?.classCode && (
                  <Chip
                    size="small"
                    variant="outlined"
                    label={getBieImportClassLabel(firstRecord.classCode)}
                  />
                )}

                {firstRecord?.sourceSheet && (
                  <Chip size="small" label={firstRecord.sourceSheet} />
                )}

                <Typography variant="body2" color="text.secondary">
                  {this.formatNumber(this.state.records.length)}
                  {' displayed record(s)'}
                </Typography>
              </Stack>
            </Box>
          </Stack>

          <Stack
            direction="row"
            spacing={1}
            justifyContent={{
              xs: 'space-between',
              sm: 'flex-end',
            }}
          >
            {firstRecord?.loadSessionId && this.props.onOpenWorkbench && (
              <Button
                variant="outlined"
                disabled={this.isBusy()}
                onClick={() =>
                  this.props.onOpenWorkbench?.(firstRecord.loadSessionId!)
                }
              >
                Open Workbench
              </Button>
            )}

            <Tooltip title="Refresh records" arrow>
              <span>
                <IconButton
                  disabled={this.isBusy()}
                  onClick={this.handleRefresh}
                >
                  <RefreshOutlinedIcon />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        </Stack>
      </Paper>
    );
  }

  /* ==========================================================
   * Filters
   * ==========================================================
   */

  private renderFilters(): React.ReactNode {
    const disabled = this.isBusy();

    return (
      <Stack spacing={1.5}>
        <Stack direction="row" spacing={1} alignItems="center">
          <FilterAltOutlinedIcon color="action" fontSize="small" />

          <Typography variant="subtitle2" fontWeight={700}>
            Record Filters
          </Typography>

          {this.getActiveFilterCount() > 0 && (
            <Chip
              size="small"
              color="primary"
              label={`${this.getActiveFilterCount()} active`}
            />
          )}
        </Stack>

        <Box
          sx={{
            display: 'grid',

            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, minmax(0, 1fr))',
              lg: 'repeat(3, minmax(0, 240px)) minmax(280px, 1fr)',
            },

            gap: 1.5,
          }}
        >
          <TextField
            select
            size="small"
            fullWidth
            label="Validation State"
            value={this.state.filters.validationCode}
            disabled={disabled}
            onChange={this.handleValidationCodeChange}
          >
            <MenuItem value="">All Validation States</MenuItem>

            <MenuItem value="PENDING">Pending</MenuItem>

            <MenuItem value="VALID">Valid</MenuItem>

            <MenuItem value="WARNING">Warning</MenuItem>

            <MenuItem value="FAILED">Failed</MenuItem>
          </TextField>

          <TextField
            select
            size="small"
            fullWidth
            label="Resolution"
            value={this.state.filters.resolutionStatus}
            disabled={disabled}
            onChange={this.handleResolutionStatusChange}
          >
            <MenuItem value="">All Records</MenuItem>

            <MenuItem value="RESOLVED">Resolved</MenuItem>

            <MenuItem value="UNRESOLVED">Unresolved</MenuItem>
          </TextField>

          <TextField
            select
            size="small"
            fullWidth
            label="Record Class"
            value={this.state.filters.recordClassCode}
            disabled={disabled}
            onChange={this.handleRecordClassCodeChange}
          >
            <MenuItem value="">All Record Classes</MenuItem>

            {this.getRecordClassOptions().map((recordClassCode) => (
              <MenuItem key={recordClassCode} value={recordClassCode}>
                {this.formatCodeLabel(recordClassCode)}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            size="small"
            fullWidth
            label="Search staged records"
            placeholder={'Metric, Product, Location, Period, Reference…'}
            value={this.state.filters.search}
            disabled={disabled}
            onChange={this.handleSearchChange}
            onKeyDown={this.handleSearchKeyDown}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchOutlinedIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        <Stack direction="row" spacing={1} justifyContent="flex-end">
          <Button
            variant="contained"
            disabled={disabled}
            startIcon={<FilterAltOutlinedIcon />}
            onClick={this.handleApplyFilters}
          >
            Apply
          </Button>

          <Button
            variant="outlined"
            color="inherit"
            disabled={disabled || this.getActiveFilterCount() === 0}
            startIcon={<ClearOutlinedIcon />}
            onClick={this.handleClearFilters}
          >
            Clear
          </Button>
        </Stack>
      </Stack>
    );
  }

  /* ==========================================================
   * Records table
   * ==========================================================
   */

  private renderRecordsTable(): React.ReactNode {
    if (this.state.records.length === 0) {
      return this.renderEmptyState();
    }

    return (
      <TableContainer>
        <Table
          size="small"
          sx={{
            minWidth: 1500,
          }}
        >
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox" />

              <TableCell>Source</TableCell>

              <TableCell>Validation</TableCell>

              <TableCell>Resolution</TableCell>

              <TableCell>Record Class</TableCell>

              <TableCell>Metric / Driver</TableCell>

              <TableCell>Product / Entity</TableCell>

              <TableCell>Location / Account</TableCell>

              <TableCell>Period</TableCell>

              <TableCell align="right">Value</TableCell>

              <TableCell>Unit</TableCell>

              <TableCell align="right">Issues</TableCell>

              <TableCell align="right">Action</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {this.state.records.map((record) => this.renderRecordRows(record))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  }

  private renderRecordRows(
    record: IBieOnboardingImportRecord
  ): React.ReactNode {
    const recordId = String(record.rid);

    const expanded = this.state.expandedRecordId === recordId;

    const selected = this.state.selectedRecordId === recordId;

    return (
      <React.Fragment key={recordId}>
        <TableRow
          hover
          selected={selected}
          onClick={() =>
            this.setState({
              selectedRecordId: recordId,
            })
          }
          sx={{
            cursor: 'pointer',

            '& > *': {
              borderBottom: expanded ? 0 : undefined,
            },
          }}
        >
          <TableCell padding="checkbox">
            <Tooltip
              title={expanded ? 'Collapse details' : 'Expand raw details'}
              arrow
            >
              <IconButton
                size="small"
                onClick={(event) => this.handleToggleExpanded(event, record)}
              >
                {expanded ? (
                  <ExpandLessOutlinedIcon />
                ) : (
                  <ExpandMoreOutlinedIcon />
                )}
              </IconButton>
            </Tooltip>
          </TableCell>

          <TableCell>
            <Stack spacing={0.15}>
              <Typography variant="body2" fontWeight={700}>
                {record.sourceSheet || 'Unknown Sheet'}
              </Typography>

              <Typography variant="caption" color="text.secondary">
                Row {this.formatNumber(record.sourceRow)}
              </Typography>

              {record.sourceReference && (
                <Typography
                  variant="caption"
                  color="text.disabled"
                  noWrap
                  title={record.sourceReference}
                  sx={{
                    maxWidth: 180,
                  }}
                >
                  {record.sourceReference}
                </Typography>
              )}
            </Stack>
          </TableCell>

          <TableCell>
            <BieOnboardingStatusChip
              kind="VALIDATION_STATE"
              value={record.validationCode}
              label={record.validationName || undefined}
            />
          </TableCell>

          <TableCell>
            <Chip
              size="small"
              variant="outlined"
              color={record.isResolved ? 'success' : 'warning'}
              icon={record.isResolved ? undefined : <LinkOffOutlinedIcon />}
              label={record.isResolved ? 'Resolved' : 'Unresolved'}
            />
          </TableCell>

          <TableCell>
            <Stack spacing={0.15}>
              <Typography variant="body2" fontWeight={600}>
                {record.recordClassName ||
                  this.formatCodeLabel(record.recordClassCode) ||
                  '—'}
              </Typography>

              {record.recordClassCode && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  fontFamily="monospace"
                >
                  {record.recordClassCode}
                </Typography>
              )}
            </Stack>
          </TableCell>

          <TableCell>
            {this.renderCodeName(record.driverCode, record.driverName)}
          </TableCell>

          <TableCell>
            {this.renderCombinedDimension(
              record.productCode,
              record.productName,
              record.entityCode,
              record.entityName
            )}
          </TableCell>

          <TableCell>
            {this.renderCombinedDimension(
              record.locationCode,
              record.locationName,
              record.accountCode,
              record.accountName
            )}
          </TableCell>

          <TableCell>
            <Stack spacing={0.15}>
              <Typography variant="body2" fontWeight={600}>
                {this.formatDate(record.periodDate)}
              </Typography>

              {record.periodCode && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  fontFamily="monospace"
                >
                  {record.periodCode}
                </Typography>
              )}
            </Stack>
          </TableCell>

          <TableCell align="right">
            <Typography variant="body2" fontWeight={700} fontFamily="monospace">
              {this.formatSourceValue(record.sourceValue)}
            </Typography>
          </TableCell>

          <TableCell>
            {record.unitCode ? (
              <Chip
                size="small"
                variant="outlined"
                label={record.unitCode}
                title={record.unitName}
              />
            ) : (
              <Typography variant="body2" color="text.disabled">
                —
              </Typography>
            )}
          </TableCell>

          <TableCell align="right">
            <Stack direction="row" spacing={0.5} justifyContent="flex-end">
              <Chip
                size="small"
                variant="outlined"
                color={
                  record.blockingIssueCount > 0
                    ? 'error'
                    : record.warningIssueCount > 0
                      ? 'warning'
                      : 'default'
                }
                label={this.formatNumber(record.issueCount)}
              />

              {record.blockingIssueCount > 0 && (
                <Chip
                  size="small"
                  color="error"
                  label={`${this.formatNumber(
                    record.blockingIssueCount
                  )} blocking`}
                />
              )}
            </Stack>
          </TableCell>

          <TableCell align="right">
            {this.props.onOpenImportItem && (
              <Tooltip title="Open staged record" arrow>
                <IconButton
                  size="small"
                  onClick={(event) => this.handleOpenRecord(event, record)}
                >
                  <OpenInNewOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </TableCell>
        </TableRow>

        <TableRow>
          <TableCell
            colSpan={13}
            sx={{
              py: 0,

              backgroundColor: 'action.hover',
            }}
          >
            <Collapse in={expanded} timeout="auto" unmountOnExit>
              {this.renderExpandedDetails(record)}
            </Collapse>
          </TableCell>
        </TableRow>
      </React.Fragment>
    );
  }

  /* ==========================================================
   * Expanded details
   * ==========================================================
   */

  private renderExpandedDetails(
    record: IBieOnboardingImportRecord
  ): React.ReactNode {
    return (
      <Box
        sx={{
          px: 1,
          py: 2,
        }}
      >
        <Stack spacing={2}>
          <Box
            sx={{
              display: 'grid',

              gridTemplateColumns: {
                xs: '1fr',
                md: 'repeat(4, minmax(0, 1fr))',
              },

              gap: 1.25,
            }}
          >
            {this.renderDetailValue('Import Item ID', String(record.rid))}

            {this.renderDetailValue(
              'Import',
              record.importCode || String(record.importId)
            )}

            {this.renderDetailValue(
              'Validation State',
              record.validationName || record.validationCode
            )}

            {this.renderDetailValue(
              'Resolution Status',
              record.resolutionStatus ||
                (record.isResolved ? 'RESOLVED' : 'UNRESOLVED')
            )}
          </Box>

          <Box
            sx={{
              display: 'grid',

              gridTemplateColumns: {
                xs: '1fr',
                lg: 'repeat(3, minmax(0, 1fr))',
              },

              gap: 1.5,
            }}
          >
            {this.renderJsonBlock('Raw Workbook Data', record.rawData)}

            {this.renderJsonBlock(
              'Validation Details',
              record.validationDetails
            )}

            {this.renderJsonBlock('Additional Information', record.extraInfo)}
          </Box>
        </Stack>
      </Box>
    );
  }

  private renderDetailValue(label: string, value: string): React.ReactNode {
    return (
      <Box
        sx={{
          p: 1.25,

          border: '1px solid',

          borderColor: 'divider',

          borderRadius: 1.5,

          backgroundColor: 'background.paper',
        }}
      >
        <Typography variant="caption" color="text.secondary" fontWeight={700}>
          {label}
        </Typography>

        <Typography
          variant="body2"
          fontWeight={600}
          sx={{
            mt: 0.25,
            overflowWrap: 'anywhere',
          }}
        >
          {value || '—'}
        </Typography>
      </Box>
    );
  }

  private renderJsonBlock(
    label: string,
    value: Record<string, unknown> | null | undefined
  ): React.ReactNode {
    const hasValue = value && Object.keys(value).length > 0;

    return (
      <Box
        sx={{
          minWidth: 0,
        }}
      >
        <Typography variant="caption" color="text.secondary" fontWeight={700}>
          {label}
        </Typography>

        <Box
          component="pre"
          sx={{
            m: 0,
            mt: 0.5,

            minHeight: 130,
            maxHeight: 300,

            p: 1.5,

            overflow: 'auto',

            border: '1px solid',

            borderColor: 'divider',

            borderRadius: 1.5,

            backgroundColor: 'background.paper',

            fontSize: 12,
            lineHeight: 1.5,

            whiteSpace: 'pre-wrap',

            overflowWrap: 'anywhere',
          }}
        >
          {hasValue ? JSON.stringify(value, null, 2) : 'No data available.'}
        </Box>
      </Box>
    );
  }

  /* ==========================================================
   * Loading and empty states
   * ==========================================================
   */

  private renderLoadingTable(): React.ReactNode {
    return (
      <Box>
        <LinearProgress />

        <TableContainer>
          <Table
            size="small"
            sx={{
              minWidth: 1500,
            }}
          >
            <TableHead>
              <TableRow>
                {Array.from({
                  length: 13,
                }).map((_, index) => (
                  <TableCell key={index}>
                    <Skeleton width={90} />
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {Array.from({
                length: 7,
              }).map((_, rowIndex) => (
                <TableRow key={rowIndex}>
                  {Array.from({
                    length: 13,
                  }).map((__, cellIndex) => (
                    <TableCell key={cellIndex}>
                      <Skeleton
                        variant={cellIndex === 0 ? 'circular' : 'text'}
                        width={cellIndex === 0 ? 28 : 100}
                        height={cellIndex === 0 ? 28 : undefined}
                      />
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  }

  private renderEmptyState(): React.ReactNode {
    return (
      <Box
        sx={{
          py: 8,
          px: 3,

          textAlign: 'center',

          borderTop: '1px solid',

          borderColor: 'divider',
        }}
      >
        <DatasetOutlinedIcon
          color="disabled"
          sx={{
            fontSize: 54,
          }}
        />

        <Typography
          variant="h6"
          fontWeight={700}
          sx={{
            mt: 1,
          }}
        >
          No staged records found
        </Typography>

        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            mt: 0.75,
          }}
        >
          The selected filters did not return any active Import Items.
        </Typography>

        {this.getActiveFilterCount() > 0 && (
          <Button
            variant="outlined"
            color="inherit"
            startIcon={<ClearOutlinedIcon />}
            sx={{
              mt: 2,
            }}
            onClick={this.handleClearFilters}
          >
            Clear Filters
          </Button>
        )}
      </Box>
    );
  }

  /* ==========================================================
   * Pagination
   * ==========================================================
   */

  private renderPagination(): React.ReactNode {
    const currentPage = Math.floor(
      this.state.pageOffset / this.state.pageLimit
    );

    const hasNextPage = this.state.records.length >= this.state.pageLimit;

    return (
      <TablePagination
        component="div"
        count={-1}
        page={currentPage}
        rowsPerPage={this.state.pageLimit}
        rowsPerPageOptions={[25, 50, 100, 250]}
        disabled={this.isBusy()}
        nextIconButtonProps={{
          disabled: this.isBusy() || !hasNextPage,
        }}
        backIconButtonProps={{
          disabled: this.isBusy() || currentPage <= 0,
        }}
        labelDisplayedRows={({ from, to }) => `${from}–${to}`}
        onPageChange={this.handlePageChange}
        onRowsPerPageChange={this.handlePageLimitChange}
      />
    );
  }

  /* ==========================================================
   * Data loading
   * ==========================================================
   */

  private async loadRecords(initialLoad = false): Promise<void> {
    const requestVersion = ++this.requestVersion;

    this.setState({
      loading: initialLoad,
      refreshing: !initialLoad,
      errorMessage: '',
    });

    try {
      // const endpoint =
      //   this.props.recordsEndpoint ||
      //   DEFAULT_IMPORT_ITEMS_ENDPOINT;

      // const response =
      //   await this.httpClient.get<
      //     IBieApiResponse<
      //       Record<string, unknown>[]
      //     >
      //   >(
      //     endpoint,
      //     {
      //       params: {
      //         importId:
      //           this.props.importId,

      //         validationCode:
      //           this.state.filters
      //             .validationCode ||
      //           undefined,

      //         resolutionStatus:
      //           this.state.filters
      //             .resolutionStatus ||
      //           undefined,

      //         recordClassCode:
      //           this.state.filters
      //             .recordClassCode ||
      //           undefined,

      //         search:
      //           this.state.filters
      //             .search ||
      //           undefined,

      //         status:
      //           1,

      //         pageOffset:
      //           this.state.pageOffset,

      //         pageLimit:
      //           this.state.pageLimit,
      //       },
      //     },
      //   );

      // if (
      //   !this.mounted ||
      //   requestVersion !==
      //   this.requestVersion
      // ) {
      //   return;
      // }

      // if (
      //   !response.data ||
      //   response.data.success !== true
      // ) {
      //   throw new Error(
      //     response.data?.message ||
      //     'The Import Item request was not successful.',
      //   );
      // }

      // const rawRecords =
      //   Array.isArray(
      //     response.data.data,
      //   )
      //     ? response.data.data
      //     : [];

      // const records =
      //   rawRecords.map(
      //     (record:any) =>
      //       this.normalizeRecord(
      //         record,
      //       ),
      //   );

      const importId = this.resolveImportId();

      if (!importId || Number(importId) <= 0) {
        throw new Error('The Import Records Workbench requires a valid Import ID.');
      }

      const request = {
        importId: importId,

        validationCode: this.state.filters.validationCode || undefined,

        resolutionStatus: this.state.filters.resolutionStatus || undefined,

        recordClassCode: this.state.filters.recordClassCode || undefined,

        search: this.state.filters.search || undefined,

        status: 1,

        pageOffset: this.state.pageOffset,

        pageLimit: this.state.pageLimit,
      };

      const records = await fetchBieOnboardingImportItems(request);

      if (!this.mounted || requestVersion !== this.requestVersion) {
        return;
      }

      this.setState({
        records,

        loading: false,
        refreshing: false,

        errorMessage: '',

        expandedRecordId: '',
        selectedRecordId: '',
      });
    } catch (error) {
      if (!this.mounted || requestVersion !== this.requestVersion) {
        return;
      }

      this.setState({
        loading: false,
        refreshing: false,

        errorMessage: this.getErrorMessage(error),
      });
    }
  }

  /* ==========================================================
   * Filter handlers
   * ==========================================================
   */

  private readonly handleValidationCodeChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    this.setState({
      filters: {
        ...this.state.filters,

        validationCode: event.target.value,
      },
    });
  };

  private readonly handleResolutionStatusChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    this.setState({
      filters: {
        ...this.state.filters,

        resolutionStatus: event.target.value,
      },
    });
  };

  private readonly handleRecordClassCodeChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    this.setState({
      filters: {
        ...this.state.filters,

        recordClassCode: event.target.value,
      },
    });
  };

  private readonly handleSearchChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    this.setState({
      filters: {
        ...this.state.filters,

        search: event.target.value,
      },
    });
  };

  private readonly handleSearchKeyDown = (
    event: React.KeyboardEvent<HTMLInputElement>
  ): void => {
    if (event.key !== 'Enter') {
      return;
    }

    event.preventDefault();

    this.handleApplyFilters();
  };

  private readonly handleApplyFilters = (): void => {
    this.setState(
      {
        pageOffset: 0,
      },
      () => {
        void this.loadRecords(false);
      }
    );
  };

  private readonly handleClearFilters = (): void => {
    this.setState(
      {
        filters: {
          validationCode: '',
          resolutionStatus: '',
          recordClassCode: '',
          search: '',
        },

        pageOffset: 0,
      },
      () => {
        void this.loadRecords(false);
      }
    );
  };

  /* ==========================================================
   * Interaction handlers
   * ==========================================================
   */

  private readonly handleRefresh = (): void => {
    void this.loadRecords(false);
  };

  private handleToggleExpanded(
    event: React.MouseEvent<HTMLButtonElement>,
    record: IBieOnboardingImportRecord
  ): void {
    event.preventDefault();
    event.stopPropagation();

    const recordId = String(record.rid);

    this.setState((currentState) => ({
      expandedRecordId:
        currentState.expandedRecordId === recordId ? '' : recordId,

      selectedRecordId: recordId,
    }));
  }

  private handleOpenRecord(
    event: React.MouseEvent<HTMLButtonElement>,
    record: IBieOnboardingImportRecord
  ): void {
    event.preventDefault();
    event.stopPropagation();

    this.setState({
      selectedRecordId: String(record.rid),
    });

    if (this.props.onOpenImportItem) {
      this.props.onOpenImportItem(record.rid, record);
      return;
    }

    const setParam = this.props.onParam('set');
    setParam('loadsessionid', record.loadSessionId || this.props.params?.loadsessionid || null);
    setParam('importid', record.importId || this.resolveImportId());
    setParam('importitemid', record.rid);
    setParam('recordclasscode', record.recordClassCode || null);
    setParam('sourcerow', record.sourceRow || null);
    setParam('stp', record.stamp || this.props.params?.stp || null);
    this.props.setPage(PAGE_IMPORT_ITEM);
  }

  private readonly handlePageChange = (_event: unknown, page: number): void => {
    this.setState(
      {
        pageOffset: page * this.state.pageLimit,
      },
      () => {
        void this.loadRecords(false);
      }
    );
  };

  private readonly handlePageLimitChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ): void => {
    const pageLimit = Number(event.target.value);

    if (!Number.isInteger(pageLimit) || pageLimit <= 0) {
      return;
    }

    this.setState(
      {
        pageLimit,
        pageOffset: 0,
      },
      () => {
        void this.loadRecords(false);
      }
    );
  };

  /* ==========================================================
   * Record normalization
   *
   * Supports both long descriptive field names and abbreviated
   * vw_ field aliases.
   * ==========================================================
   */

  // private normalizeRecord(
  //   source:
  //     Record<string, unknown>,
  // ): IBieOnboardingImportRecord {
  //   const read =
  //     (
  //       ...keys: string[]
  //     ): unknown =>
  //       this.readValue(
  //         source,
  //         keys,
  //       );

  //   const recordId =
  //     this.requireRecordId(
  //       read(
  //         'rid',
  //         'recid',
  //         'id',
  //       ),
  //     );

  //   const resolutionStatus =
  //     this.readText(
  //       read(
  //         'resolutionstatus',
  //         'resolutionStatus',
  //         'rst',
  //       ),
  //     ).toUpperCase();

  //   const explicitResolved =
  //     read(
  //       'isresolved',
  //       'isResolved',
  //       'irs',
  //     );

  //   const isResolved =
  //     explicitResolved !== undefined &&
  //       explicitResolved !== null
  //       ? this.readBoolean(
  //         explicitResolved,
  //       )
  //       : resolutionStatus ===
  //       'RESOLVED';

  //   return {
  //     rid:
  //       recordId,

  //     rix:
  //       this.readText(
  //         read(
  //           'rix',
  //           'recix',
  //         ),
  //       ),

  //     importId:
  //       this.readId(
  //         read(
  //           'importid',
  //           'importId',
  //           'imi',
  //         ),
  //       ) ||
  //       this.props.importId,

  //     importIx:
  //       this.readText(
  //         read(
  //           'importix',
  //           'importIx',
  //           'imx',
  //         ),
  //       ),

  //     importCode:
  //       this.readText(
  //         read(
  //           'importcode',
  //           'importCode',
  //           'imc',
  //         ),
  //       ),

  //     importName:
  //       this.readText(
  //         read(
  //           'importname',
  //           'importName',
  //           'imn',
  //         ),
  //       ),

  //     loadSessionId:
  //       this.readId(
  //         read(
  //           'loadsessionid',
  //           'loadSessionId',
  //           'lsi',
  //         ),
  //       ),

  //     loadSessionCode:
  //       this.readText(
  //         read(
  //           'loadsessioncode',
  //           'loadSessionCode',
  //           'lsc',
  //         ),
  //       ),

  //     loadSessionName:
  //       this.readText(
  //         read(
  //           'loadsessionname',
  //           'loadSessionName',
  //           'lsn',
  //         ),
  //       ),

  //     classCode:
  //       this.readText(
  //         read(
  //           'classcode',
  //           'classCode',
  //           'clc',
  //         ),
  //       ).toUpperCase(),

  //     className:
  //       this.readText(
  //         read(
  //           'classname',
  //           'className',
  //           'cln',
  //         ),
  //       ),

  //     recordClassId:
  //       this.readId(
  //         read(
  //           'recordclassid',
  //           'recordClassId',
  //           'rci',
  //         ),
  //       ),

  //     recordClassCode:
  //       this.readText(
  //         read(
  //           'recordclasscode',
  //           'recordClassCode',
  //           'rcc',
  //         ),
  //       ).toUpperCase(),

  //     recordClassName:
  //       this.readText(
  //         read(
  //           'recordclassname',
  //           'recordClassName',
  //           'rcn',
  //         ),
  //       ),

  //     sourceSheet:
  //       this.readText(
  //         read(
  //           'sourcesheet',
  //           'sourceSheet',
  //           'ssh',
  //         ),
  //       ),

  //     sourceRow:
  //       this.readNumber(
  //         read(
  //           'sourcerow',
  //           'sourceRow',
  //           'srw',
  //         ),
  //       ),

  //     sourceReference:
  //       this.readText(
  //         read(
  //           'sourcereference',
  //           'sourceReference',
  //           'srf',
  //         ),
  //       ),

  //     validationStateId:
  //       this.readId(
  //         read(
  //           'validationstateid',
  //           'validationStateId',
  //           'vsi',
  //         ),
  //       ),

  //     validationCode:
  //       this.readText(
  //         read(
  //           'validationcode',
  //           'validationCode',
  //           'vsc',
  //           'vlc',
  //         ),
  //       ).toUpperCase(),

  //     validationName:
  //       this.readText(
  //         read(
  //           'validationname',
  //           'validationName',
  //           'vsn',
  //           'vln',
  //         ),
  //       ),

  //     resolutionStatus:
  //       resolutionStatus ||
  //       (
  //         isResolved
  //           ? 'RESOLVED'
  //           : 'UNRESOLVED'
  //       ),

  //     isResolved,

  //     driverId:
  //       this.readId(
  //         read(
  //           'driverid',
  //           'driverId',
  //           'dri',
  //         ),
  //       ),

  //     driverCode:
  //       this.readText(
  //         read(
  //           'drivercode',
  //           'driverCode',
  //           'drc',
  //           'metriccode',
  //           'metricCode',
  //         ),
  //       ),

  //     driverName:
  //       this.readText(
  //         read(
  //           'drivername',
  //           'driverName',
  //           'drn',
  //           'metricname',
  //           'metricName',
  //         ),
  //       ),

  //     productId:
  //       this.readId(
  //         read(
  //           'productid',
  //           'productId',
  //           'pdi',
  //         ),
  //       ),

  //     productCode:
  //       this.readText(
  //         read(
  //           'productcode',
  //           'productCode',
  //           'pdc',
  //         ),
  //       ),

  //     productName:
  //       this.readText(
  //         read(
  //           'productname',
  //           'productName',
  //           'pdn',
  //         ),
  //       ),

  //     entityId:
  //       this.readId(
  //         read(
  //           'entityid',
  //           'entityId',
  //           'eni',
  //         ),
  //       ),

  //     entityCode:
  //       this.readText(
  //         read(
  //           'entitycode',
  //           'entityCode',
  //           'enc',
  //         ),
  //       ),

  //     entityName:
  //       this.readText(
  //         read(
  //           'entityname',
  //           'entityName',
  //           'enn',
  //         ),
  //       ),

  //     locationId:
  //       this.readId(
  //         read(
  //           'locationid',
  //           'locationId',
  //           'lci',
  //         ),
  //       ),

  //     locationCode:
  //       this.readText(
  //         read(
  //           'locationcode',
  //           'locationCode',
  //           'lcc',
  //         ),
  //       ),

  //     locationName:
  //       this.readText(
  //         read(
  //           'locationname',
  //           'locationName',
  //           'lcn',
  //         ),
  //       ),

  //     accountId:
  //       this.readId(
  //         read(
  //           'accountid',
  //           'accountId',
  //           'aci',
  //         ),
  //       ),

  //     accountCode:
  //       this.readText(
  //         read(
  //           'accountcode',
  //           'accountCode',
  //           'acc',
  //         ),
  //       ),

  //     accountName:
  //       this.readText(
  //         read(
  //           'accountname',
  //           'accountName',
  //           'acn',
  //         ),
  //       ),

  //     periodId:
  //       this.readId(
  //         read(
  //           'periodid',
  //           'periodId',
  //           'pei',
  //         ),
  //       ),

  //     periodCode:
  //       this.readText(
  //         read(
  //           'periodcode',
  //           'periodCode',
  //           'pec',
  //         ),
  //       ),

  //     periodDate:
  //       this.readNullableText(
  //         read(
  //           'perioddate',
  //           'periodDate',
  //           'ped',
  //         ),
  //       ),

  //     unitId:
  //       this.readId(
  //         read(
  //           'unitid',
  //           'unitId',
  //           'uni',
  //         ),
  //       ),

  //     unitCode:
  //       this.readText(
  //         read(
  //           'unitcode',
  //           'unitCode',
  //           'unc',
  //         ),
  //       ),

  //     unitName:
  //       this.readText(
  //         read(
  //           'unitname',
  //           'unitName',
  //           'unn',
  //         ),
  //       ),

  //     sourceValue:
  //       this.readSourceValue(
  //         read(
  //           'sourcevalue',
  //           'sourceValue',
  //           'val',
  //         ),
  //       ),

  //     issueCount:
  //       this.readNumber(
  //         read(
  //           'issuecount',
  //           'issueCount',
  //           'isc',
  //         ),
  //       ),

  //     blockingIssueCount:
  //       this.readNumber(
  //         read(
  //           'blockingissuecount',
  //           'blockingIssueCount',
  //           'bic',
  //         ),
  //       ),

  //     warningIssueCount:
  //       this.readNumber(
  //         read(
  //           'warningissuecount',
  //           'warningIssueCount',
  //           'wic',
  //         ),
  //       ),

  //     rawData:
  //       this.readObject(
  //         read(
  //           'rawdata',
  //           'rawData',
  //           'rwd',
  //         ),
  //       ),

  //     validationDetails:
  //       this.readObject(
  //         read(
  //           'validationdetails',
  //           'validationDetails',
  //           'vdd',
  //         ),
  //       ),

  //     extraInfo:
  //       this.readObject(
  //         read(
  //           'extrainfo',
  //           'extraInfo',
  //           'exi',
  //         ),
  //       ),

  //     status:
  //       this.readNumber(
  //         read(
  //           'status',
  //           'sts',
  //         ),
  //       ),

  //     stamp:
  //       this.readText(
  //         read(
  //           'stamp',
  //           'stp',
  //         ),
  //       ),

  //     source,
  //   };
  // }

  /* ==========================================================
   * Display helpers
   * ==========================================================
   */

  private renderCodeName(code: string, name: string): React.ReactNode {
    if (!code && !name) {
      return (
        <Typography variant="body2" color="text.disabled">
          —
        </Typography>
      );
    }

    return (
      <Stack spacing={0.15}>
        <Typography variant="body2" fontWeight={600}>
          {name || code}
        </Typography>

        {code && (
          <Typography
            variant="caption"
            color="text.secondary"
            fontFamily="monospace"
          >
            {code}
          </Typography>
        )}
      </Stack>
    );
  }

  private renderCombinedDimension(
    primaryCode: string,
    primaryName: string,
    secondaryCode: string,
    secondaryName: string
  ): React.ReactNode {
    if (primaryCode || primaryName) {
      return this.renderCodeName(primaryCode, primaryName);
    }

    return this.renderCodeName(secondaryCode, secondaryName);
  }

  private getRecordClassOptions(): string[] {
    return Array.from(
      new Set(
        this.state.records
          .map((record) => record.recordClassCode)
          .filter(Boolean)
      )
    ).sort();
  }

  private getActiveFilterCount(): number {
    return Object.values(this.state.filters).filter(
      (value) => String(value).trim() !== ''
    ).length;
  }

  private isBusy(): boolean {
    return this.state.loading || this.state.refreshing;
  }

  private getErrorMessage(error: unknown): string {
    // if (axios.isAxiosError(error)) {
    //   const response =
    //     error.response?.data as {
    //       error?: {
    //         message?: string;
    //       };

    //       message?: string;
    //     } | undefined;

    //   return (
    //     response?.error?.message ||
    //     response?.message ||
    //     error.message ||
    //     'Unable to retrieve staged Import Items.'
    //   );
    // }

    if (error instanceof Error) {
      return error.message;
    }

    return (
      'An unexpected error occurred while retrieving the ' +
      'staged Import Items.'
    );
  }

  private formatCodeLabel(value: string): string {
    if (!value) {
      return '';
    }

    return value
      .replace(/_/g, ' ')
      .toLowerCase()
      .replace(/\b\w/g, (character) => character.toUpperCase());
  }

  private formatNumber(value: unknown): string {
    const numericValue = Number(value);

    if (!Number.isFinite(numericValue)) {
      return '0';
    }

    return new Intl.NumberFormat().format(numericValue);
  }

  private formatSourceValue(value: unknown): string {
    if (value === null || value === undefined || value === '') {
      return '—';
    }

    const numericValue = Number(value);

    if (Number.isFinite(numericValue)) {
      return new Intl.NumberFormat(undefined, {
        maximumFractionDigits: 6,
      }).format(numericValue);
    }

    return String(value);
  }

  private formatDate(value: string | null | undefined): string {
    if (!value) {
      return '—';
    }

    const normalized = String(value).trim();

    const match = normalized.match(/^(\d{4})-(\d{2})-(\d{2})/);

    if (!match) {
      return normalized;
    }

    const date = new Date(
      Number(match[1]),
      Number(match[2]) - 1,
      Number(match[3])
    );

    if (Number.isNaN(date.getTime())) {
      return normalized;
    }

    return new Intl.DateTimeFormat(undefined, {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
    }).format(date);
  }

  /* ==========================================================
   * Normalization helpers
   * ==========================================================
   */

  // private readValue(
  //   source:
  //     Record<string, unknown>,
  //   keys: string[],
  // ): unknown {
  //   for (const key of keys) {
  //     if (
  //       Object.prototype
  //         .hasOwnProperty
  //         .call(
  //           source,
  //           key,
  //         )
  //     ) {
  //       return source[key];
  //     }

  //     const matchedKey =
  //       Object.keys(source)
  //         .find(
  //           (candidate) =>
  //             candidate.toLowerCase() ===
  //             key.toLowerCase(),
  //         );

  //     if (matchedKey) {
  //       return source[matchedKey];
  //     }
  //   }

  //   return undefined;
  // }

  // private requireRecordId(
  //   value: unknown,
  // ): BieRecordId {
  //   const normalized =
  //     String(value ?? '')
  //       .trim();

  //   if (
  //     !normalized ||
  //     !/^\d+$/.test(normalized)
  //   ) {
  //     throw new Error(
  //       'The Import Item response contains an invalid record ID.',
  //     );
  //   }

  //   const numericValue =
  //     Number(normalized);

  //   return Number.isSafeInteger(
  //     numericValue,
  //   )
  //     ? numericValue
  //     : normalized;
  // }

  // private readId(
  //   value: unknown,
  // ): BieRecordId | null {
  //   if (
  //     value === null ||
  //     value === undefined ||
  //     String(value).trim() === ''
  //   ) {
  //     return null;
  //   }

  //   const normalized =
  //     String(value).trim();

  //   if (!/^\d+$/.test(normalized)) {
  //     return null;
  //   }

  //   const numericValue =
  //     Number(normalized);

  //   return Number.isSafeInteger(
  //     numericValue,
  //   )
  //     ? numericValue
  //     : normalized;
  // }

  // private readText(
  //   value: unknown,
  // ): string {
  //   if (
  //     value === null ||
  //     value === undefined
  //   ) {
  //     return '';
  //   }

  //   return String(value).trim();
  // }

  // private readNullableText(
  //   value: unknown,
  // ): string | null {
  //   const normalized =
  //     this.readText(value);

  //   return normalized || null;
  // }

  // private readNumber(
  //   value: unknown,
  // ): number {
  //   const normalized =
  //     Number(value);

  //   return Number.isFinite(
  //     normalized,
  //   )
  //     ? normalized
  //     : 0;
  // }

  // private readBoolean(
  //   value: unknown,
  // ): boolean {
  //   if (
  //     value === true ||
  //     value === 1 ||
  //     value === '1'
  //   ) {
  //     return true;
  //   }

  //   const normalized =
  //     String(value ?? '')
  //       .trim()
  //       .toLowerCase();

  //   return (
  //     normalized === 'true' ||
  //     normalized === 'yes' ||
  //     normalized === 'resolved'
  //   );
  // }

  // private readObject(
  //   value: unknown,
  // ): Record<string, unknown> {
  //   if (
  //     typeof value === 'object' &&
  //     value !== null &&
  //     !Array.isArray(value)
  //   ) {
  //     return value as
  //       Record<string, unknown>;
  //   }

  //   if (
  //     typeof value === 'string' &&
  //     value.trim()
  //   ) {
  //     try {
  //       const parsed =
  //         JSON.parse(value);

  //       if (
  //         typeof parsed === 'object' &&
  //         parsed !== null &&
  //         !Array.isArray(parsed)
  //       ) {
  //         return parsed as
  //           Record<string, unknown>;
  //       }
  //     } catch {
  //       return {};
  //     }
  //   }

  //   return {};
  // }

  // private readSourceValue(
  //   value: unknown,
  // ): string | number | null {
  //   if (
  //     value === null ||
  //     value === undefined ||
  //     value === ''
  //   ) {
  //     return null;
  //   }

  //   if (
  //     typeof value === 'number'
  //   ) {
  //     return value;
  //   }

  //   const normalized =
  //     String(value).trim();

  //   const numericValue =
  //     Number(normalized);

  //   return Number.isFinite(
  //     numericValue,
  //   )
  //     ? numericValue
  //     : normalized;
  // }
}
